package com.schoolconnect.app.feature.schools.edit

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Box
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.PaddingValues
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.height
import androidx.compose.ui.Alignment
import androidx.compose.foundation.shape.RoundedCornerShape
import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.runtime.collectAsState
import androidx.compose.ui.Modifier
import androidx.compose.ui.unit.dp
import androidx.compose.ui.text.input.KeyboardType
import com.schoolconnect.app.designsystem.AppDialogActionStyle
import com.schoolconnect.app.designsystem.AppDialogBody
import com.schoolconnect.app.designsystem.AppDialogDivider
import com.schoolconnect.app.designsystem.AppDialogErrorMessage
import com.schoolconnect.app.designsystem.AppDialogFooter
import com.schoolconnect.app.designsystem.AppDialogFooterAction
import com.schoolconnect.app.designsystem.AppDialogHeader
import com.schoolconnect.app.designsystem.AppDialogScaffold
import com.schoolconnect.app.feature.schools.SchoolLogoPicker
import com.schoolconnect.app.feature.schools.SearchableSchoolSelectInput
import com.schoolconnect.app.feature.schools.sanitizeSchoolPhoneInput
import com.schoolconnect.app.feature.schools.schoolCountryOptions
import com.schoolconnect.app.feature.schools.schoolStateOptions
import com.schoolconnect.app.repository.systemlookup.SystemLookupRepository
import ischoolconnect.sharedui.generated.resources.Res
import ischoolconnect.sharedui.generated.resources.info_circle
import ischoolconnect.sharedui.generated.resources.map_pin
import ischoolconnect.sharedui.generated.resources.phone_call
import org.jetbrains.compose.resources.painterResource

@Composable
fun EditSchoolScreen(
    state: EditSchoolUiState,
    onEvent: (EditSchoolUiEvent) -> Unit,
    onBack: () -> Unit
) {
    val errors = state.validationErrors
    val lookupSnapshot by SystemLookupRepository.snapshot.collectAsState()
    LaunchedEffect(lookupSnapshot.items.isEmpty()) {
        if (lookupSnapshot.items.isEmpty()) {
            SystemLookupRepository.refresh(forceRemote = true)
        }
    }
    var boardOptions by remember { mutableStateOf(emptyList<EditSchoolOption>()) }
    var typeOptions by remember { mutableStateOf(emptyList<EditSchoolOption>()) }
    var mediumOptions by remember { mutableStateOf(emptyList<EditSchoolOption>()) }
    LaunchedEffect(Unit) {
        boardOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_BOARD")
            .getOrDefault(emptyList())
            .mapNotNull { item ->
                val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
                EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
            }
        typeOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_TYPE")
            .getOrDefault(emptyList())
            .mapNotNull { item ->
                val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
                EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
            }
        mediumOptions = SystemLookupRepository.loadCategoryItems("SCHOOL_MEDIUM")
            .getOrDefault(emptyList())
            .mapNotNull { item ->
                val code = item.code?.trim()?.takeIf(String::isNotBlank) ?: return@mapNotNull null
                EditSchoolOption(code, item.displayValue?.trim().takeIf { !it.isNullOrBlank() } ?: code)
            }
    }

    val dismiss = { if (!state.isSaving && !state.isLoading) onBack() }
    AppDialogScaffold(
        onDismiss = dismiss,
        dialogWidthOverride = 1200.dp,
        maxDialogHeight = 1200.dp,
        fillDialogHeight = true,
        dismissOnClickOutside = false,
        shape = RoundedCornerShape(10.dp),
        isLoading = state.isLoading || state.isSaving,
        loadingTitle = if (state.isSaving) "Saving school" else "Loading school"
    ) {
        AppDialogHeader(
            title = "Edit School",
            subtitle = "Update school details and contact information",
            onClose = dismiss
        )
        AppDialogDivider()
        AppDialogBody(
            compactBreakpoint = 900.dp,
            showVerticalScrollbar = true,
            fillAvailable = true,
            horizontalPadding = 24.dp,
            compactHorizontalPadding = 24.dp,
            verticalPadding = 24.dp
        ) { compact ->
            if (state.errorMessage.isNotBlank()) {
                AppDialogErrorMessage(message = state.errorMessage)
            }
                EditSchoolSectionCard(
                    compact = compact,
                    iconPainter = painterResource(Res.drawable.info_circle)
                ) {
                    if (compact) {
                        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                            SchoolLogoPicker(
                                selectedFileName = state.form.schoolLogoFileName,
                                selectedBytes = state.form.schoolLogoBytes,
                                hasExistingLogo = state.form.schoolLogoUrl.isNotBlank(),
                                compact = true,
                                onSelected = { fileName, bytes ->
                                    onEvent(EditSchoolUiEvent.LogoSelected(fileName, bytes))
                                },
                                modifier = Modifier.fillMaxWidth()
                            )
                            EditSchoolTextField(
                                label = "School Name",
                                value = state.form.schoolName,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_NAME, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.schoolName.isNotBlank(),
                                errorText = if (state.showErrors) errors.schoolName else ""
                            )
                            EditSchoolDropdownField(
                                label = "Board",
                                value = state.form.board,
                                options = boardOptions,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.BOARD, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.board.isNotBlank(),
                                errorText = if (state.showErrors) errors.board else "",
                                labelResolver = ::editSchoolBoardLabel
                            )
                            EditSchoolDropdownField(
                                label = "Type",
                                value = state.form.type,
                                options = typeOptions,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.TYPE, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.type.isNotBlank(),
                                errorText = if (state.showErrors) errors.type else "",
                                labelResolver = ::editSchoolTypeLabel
                            )
                            EditSchoolDropdownField(
                                label = "Medium",
                                value = state.form.medium,
                                options = mediumOptions,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.MEDIUM, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.medium.isNotBlank(),
                                errorText = if (state.showErrors) errors.medium else "",
                                labelResolver = ::editSchoolMediumLabel
                            )
                            EditSchoolOverviewStatus(
                                value = state.form.status,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.STATUS, it))
                                },
                                compact = true
                            )
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                SchoolLogoPicker(
                                    selectedFileName = state.form.schoolLogoFileName,
                                    selectedBytes = state.form.schoolLogoBytes,
                                    hasExistingLogo = state.form.schoolLogoUrl.isNotBlank(),
                                    compact = false,
                                    onSelected = { fileName, bytes ->
                                        onEvent(EditSchoolUiEvent.LogoSelected(fileName, bytes))
                                    },
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                            Box(modifier = Modifier.weight(2f)) {
                                EditSchoolTextField(
                                    label = "School Name",
                                    value = state.form.schoolName,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_NAME, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.schoolName.isNotBlank(),
                                    errorText = if (state.showErrors) errors.schoolName else ""
                                )
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolDropdownField(
                                    label = "Board",
                                    value = state.form.board,
                                    options = boardOptions,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.BOARD, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.board.isNotBlank(),
                                    errorText = if (state.showErrors) errors.board else "",
                                    labelResolver = ::editSchoolBoardLabel
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolDropdownField(
                                    label = "Type",
                                    value = state.form.type,
                                    options = typeOptions,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.TYPE, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.type.isNotBlank(),
                                    errorText = if (state.showErrors) errors.type else "",
                                    labelResolver = ::editSchoolTypeLabel
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolDropdownField(
                                    label = "Medium",
                                    value = state.form.medium,
                                    options = mediumOptions,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.MEDIUM, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.medium.isNotBlank(),
                                    errorText = if (state.showErrors) errors.medium else "",
                                    labelResolver = ::editSchoolMediumLabel
                                )
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolOverviewStatus(
                                    value = state.form.status,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.STATUS, it))
                                    },
                                    compact = false
                                )
                            }
                            Spacer(modifier = Modifier.weight(1f))
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }

                EditSchoolSectionCard(
                    compact = compact,
                    iconPainter = painterResource(Res.drawable.map_pin)
                ) {
                    if (compact) {
                        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                            EditSchoolTextField(
                                label = "Address Line 1",
                                value = state.form.addressLine1,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.ADDRESS_LINE1, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.addressLine1.isNotBlank(),
                                errorText = if (state.showErrors) errors.addressLine1 else ""
                            )
                            EditSchoolTextField(
                                label = "Address Line 2",
                                value = state.form.addressLine2,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.ADDRESS_LINE2, it))
                                }
                            )
                            EditSchoolTextField(
                                label = "City",
                                value = state.form.city,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.CITY, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.city.isNotBlank(),
                                errorText = if (state.showErrors) errors.city else ""
                            )
                            EditSchoolTextField(
                                label = "District",
                                value = state.form.district,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.DISTRICT, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.district.isNotBlank(),
                                errorText = if (state.showErrors) errors.district else ""
                            )
                            SearchableSchoolSelectInput(
                                label = "State",
                                value = state.form.state,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.STATE, it))
                                },
                                placeholder = "Select state",
                                options = schoolStateOptions,
                                required = true,
                                error = if (state.showErrors) errors.state else ""
                            )
                            SearchableSchoolSelectInput(
                                label = "Country",
                                value = state.form.country,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.COUNTRY, it))
                                },
                                placeholder = "Select country",
                                options = schoolCountryOptions,
                                required = true,
                                error = if (state.showErrors) errors.country else ""
                            )
                            EditSchoolTextField(
                                label = "Pin Code",
                                value = state.form.pinCode,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PIN_CODE, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.pinCode.isNotBlank(),
                                errorText = if (state.showErrors) errors.pinCode else ""
                            )
                        }
                    } else {
                        EditSchoolTextField(
                            label = "Address Line 1",
                            value = state.form.addressLine1,
                            onValueChange = {
                                onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.ADDRESS_LINE1, it))
                            },
                            required = true,
                            isError = state.showErrors && errors.addressLine1.isNotBlank(),
                            errorText = if (state.showErrors) errors.addressLine1 else ""
                        )
                        EditSchoolTextField(
                            label = "Address Line 2",
                            value = state.form.addressLine2,
                            onValueChange = {
                                onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.ADDRESS_LINE2, it))
                            }
                        )
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "City",
                                    value = state.form.city,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.CITY, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.city.isNotBlank(),
                                    errorText = if (state.showErrors) errors.city else ""
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "District",
                                    value = state.form.district,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.DISTRICT, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.district.isNotBlank(),
                                    errorText = if (state.showErrors) errors.district else ""
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                SearchableSchoolSelectInput(
                                    label = "State",
                                    value = state.form.state,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.STATE, it))
                                    },
                                    placeholder = "Select state",
                                    options = schoolStateOptions,
                                    required = true,
                                    error = if (state.showErrors) errors.state else "",
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp),
                            verticalAlignment = Alignment.Top
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                SearchableSchoolSelectInput(
                                    label = "Country",
                                    value = state.form.country,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.COUNTRY, it))
                                    },
                                    placeholder = "Select country",
                                    options = schoolCountryOptions,
                                    required = true,
                                    error = if (state.showErrors) errors.country else "",
                                    modifier = Modifier.fillMaxWidth()
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Pin Code",
                                    value = state.form.pinCode,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PIN_CODE, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.pinCode.isNotBlank(),
                                    errorText = if (state.showErrors) errors.pinCode else ""
                                )
                            }
                            Spacer(modifier = Modifier.weight(1f))
                        }
                    }
                }

                EditSchoolSectionCard(
                    compact = compact,
                    iconPainter = painterResource(Res.drawable.phone_call)
                ) {
                    if (compact) {
                        Column(verticalArrangement = Arrangement.spacedBy(16.dp)) {
                            EditSchoolTextField(
                                label = "School Phone",
                                value = state.form.schoolPhone,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_PHONE, sanitizeSchoolPhoneInput(it)))
                                },
                                required = true,
                                keyboardType = KeyboardType.Phone,
                                isError = state.showErrors && errors.schoolPhone.isNotBlank(),
                                errorText = if (state.showErrors) errors.schoolPhone else ""
                            )
                            EditSchoolTextField(
                                label = "School Email",
                                value = state.form.schoolEmail,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_EMAIL, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.schoolEmail.isNotBlank(),
                                errorText = if (state.showErrors) errors.schoolEmail else ""
                            )
                            EditSchoolTextField(
                                label = "Primary Contact",
                                value = state.form.primaryContact,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_CONTACT, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.primaryContact.isNotBlank(),
                                errorText = if (state.showErrors) errors.primaryContact else ""
                            )
                            EditSchoolTextField(
                                label = "Primary Phone",
                                value = state.form.primaryPhone,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_PHONE, sanitizeSchoolPhoneInput(it)))
                                },
                                required = true,
                                keyboardType = KeyboardType.Phone,
                                isError = state.showErrors && errors.primaryPhone.isNotBlank(),
                                errorText = if (state.showErrors) errors.primaryPhone else ""
                            )
                            EditSchoolTextField(
                                label = "Primary Email",
                                value = state.form.primaryEmail,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_EMAIL, it))
                                },
                                required = true,
                                isError = state.showErrors && errors.primaryEmail.isNotBlank(),
                                errorText = if (state.showErrors) errors.primaryEmail else ""
                            )
                            EditSchoolTextField(
                                label = "Secondary Contact",
                                value = state.form.secondaryContact,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_CONTACT, it))
                                }
                            )
                            EditSchoolTextField(
                                label = "Secondary Phone",
                                value = state.form.secondaryPhone,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_PHONE, sanitizeSchoolPhoneInput(it)))
                                },
                                keyboardType = KeyboardType.Phone,
                                isError = state.showErrors && errors.secondaryPhone.isNotBlank(),
                                errorText = if (state.showErrors) errors.secondaryPhone else ""
                            )
                            EditSchoolTextField(
                                label = "Secondary Email",
                                value = state.form.secondaryEmail,
                                onValueChange = {
                                    onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_EMAIL, it))
                                },
                                isError = state.showErrors && errors.secondaryEmail.isNotBlank(),
                                errorText = if (state.showErrors) errors.secondaryEmail else ""
                            )
                        }
                    } else {
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "School Phone",
                                    value = state.form.schoolPhone,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_PHONE, sanitizeSchoolPhoneInput(it)))
                                    },
                                    required = true,
                                    keyboardType = KeyboardType.Phone,
                                    isError = state.showErrors && errors.schoolPhone.isNotBlank(),
                                    errorText = if (state.showErrors) errors.schoolPhone else ""
                                )
                            }
                            Box(modifier = Modifier.weight(2f)) {
                                EditSchoolTextField(
                                    label = "School Email",
                                    value = state.form.schoolEmail,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_EMAIL, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.schoolEmail.isNotBlank(),
                                    errorText = if (state.showErrors) errors.schoolEmail else ""
                                )
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Primary Contact",
                                    value = state.form.primaryContact,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_CONTACT, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.primaryContact.isNotBlank(),
                                    errorText = if (state.showErrors) errors.primaryContact else ""
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Primary Phone",
                                    value = state.form.primaryPhone,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_PHONE, sanitizeSchoolPhoneInput(it)))
                                    },
                                    required = true,
                                    keyboardType = KeyboardType.Phone,
                                    isError = state.showErrors && errors.primaryPhone.isNotBlank(),
                                    errorText = if (state.showErrors) errors.primaryPhone else ""
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Primary Email",
                                    value = state.form.primaryEmail,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.PRIMARY_EMAIL, it))
                                    },
                                    required = true,
                                    isError = state.showErrors && errors.primaryEmail.isNotBlank(),
                                    errorText = if (state.showErrors) errors.primaryEmail else ""
                                )
                            }
                        }
                        Row(
                            modifier = Modifier.fillMaxWidth(),
                            horizontalArrangement = Arrangement.spacedBy(16.dp)
                        ) {
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Secondary Contact",
                                    value = state.form.secondaryContact,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_CONTACT, it))
                                    }
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Secondary Phone",
                                    value = state.form.secondaryPhone,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_PHONE, sanitizeSchoolPhoneInput(it)))
                                    },
                                    keyboardType = KeyboardType.Phone,
                                    isError = state.showErrors && errors.secondaryPhone.isNotBlank(),
                                    errorText = if (state.showErrors) errors.secondaryPhone else ""
                                )
                            }
                            Box(modifier = Modifier.weight(1f)) {
                                EditSchoolTextField(
                                    label = "Secondary Email",
                                    value = state.form.secondaryEmail,
                                    onValueChange = {
                                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SECONDARY_EMAIL, it))
                                    },
                                    isError = state.showErrors && errors.secondaryEmail.isNotBlank(),
                                    errorText = if (state.showErrors) errors.secondaryEmail else ""
                                )
                            }
                        }
                    }
                }

                EditSchoolTextArea(
                    label = "School Comments",
                    value = state.form.schoolDescription,
                    onValueChange = {
                        onEvent(EditSchoolUiEvent.FieldChanged(EditSchoolField.SCHOOL_DESCRIPTION, it))
                    },
                    placeholder = "Enter school comments (optional)"
                )
        }
        AppDialogFooter(
            actions = listOf(
                AppDialogFooterAction(
                    label = "Cancel",
                    onClick = dismiss,
                    enabled = !state.isSaving
                ),
                AppDialogFooterAction(
                    label = if (state.isSaving) "Saving..." else "Save",
                    style = AppDialogActionStyle.Primary,
                    enabled = state.isDirty && !state.isSaving,
                    onClick = { onEvent(EditSchoolUiEvent.SaveClicked) }
                )
            ),
            contentPadding = PaddingValues(horizontal = 24.dp)
        )
    }
}
